import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { setInPageLoading } from "../store/LoadingSlice";

interface ErrorState {
  error: string | null;
  isLoading: boolean;
}

interface UseErrorHandlerReturn {
  error: string | null;
  isLoading: boolean;
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
  handleAsyncOperation: <T>(
    operation: () => Promise<T>,
    options?: {
      loadingMessage?: string;
      successMessage?: string;
      onSuccess?: (result: T) => void;
      onError?: (error: Error) => void;
    }
  ) => Promise<T | null>;
  clearError: () => void;
}

export const useErrorHandler = (): UseErrorHandlerReturn => {
  const [state, setState] = useState<ErrorState>({
    error: null,
    isLoading: false,
  });
  
  const dispatch = useDispatch();

  const setError = useCallback((error: string | null) => {
    setState(prev => ({ ...prev, error }));
  }, []);

  const setLoading = useCallback((isLoading: boolean) => {
    setState(prev => ({ ...prev, isLoading }));
    dispatch(setInPageLoading(isLoading));
  }, [dispatch]);

  const clearError = useCallback(() => {
    setError(null);
  }, [setError]);

  const handleAsyncOperation = useCallback(async <T>(
    operation: () => Promise<T>,
    options: {
      loadingMessage?: string;
      successMessage?: string;
      onSuccess?: (result: T) => void;
      onError?: (error: Error) => void;
    } = {}
  ): Promise<T | null> => {
    try {
      setLoading(true);
      clearError();
      
      const result = await operation();
      
      if (options.onSuccess) {
        options.onSuccess(result);
      }
      
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error 
        ? error.message 
        : "An unexpected error occurred";
      
      setError(errorMessage);
      
      if (options.onError && error instanceof Error) {
        options.onError(error);
      }
      
      console.error("Async operation failed:", error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [setLoading, clearError, setError]);

  return {
    error: state.error,
    isLoading: state.isLoading,
    setError,
    setLoading,
    handleAsyncOperation,
    clearError,
  };
};